import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import StickyNote from "./items/StickyNote";
import QuestionCard from "./items/QuestionCard";
import ContentItem from "./items/ContentItem";
import TextItem from "./items/TextItem";
import ShapeItem from "./items/ShapeItem";
import InkLayer from "./items/InkLayer";
import ConnectorLayer from "./items/ConnectorLayer";
import AddHandles from "./items/AddHandles";
import SelectionFrame from "./items/SelectionFrame";
import { c } from "../classroomColors";
import { TOOLS, PLACES_ON_CLICK, DRAGS_TO_DRAW } from "./boardTools";
import { CONNECTABLE, DIRECTIONS, RESIZABLE, isConnector, spawnPosition } from "./boardGeometry";

const STROKE_WIDTH = 2.5;
const MIN_SHAPE = 12;
/** How long the add handles stay up after the pointer leaves the item. */
const HOVER_GRACE_MS = 260;

/** Which cursor tells the truth about what a click will do. */
const cursorFor = (tool, readOnly, idleCursor = "default") => {
  if (readOnly || tool === TOOLS.SELECT) return idleCursor;
  if (tool === TOOLS.DRAW) return "crosshair";
  if (tool === TOOLS.COMMENT) return "cell";
  return "copy";
};

const ITEM_COMPONENTS = {
  sticky: StickyNote,
  text: TextItem,
  shape: ShapeItem,
  question: QuestionCard,
  content: ContentItem,
};

/**
 * The surface: turns pointer gestures into board items, according to the
 * active tool.
 *
 * It holds only the gesture in flight — the stroke or shape currently being
 * drawn. Anything finished is handed to the board through callbacks, so this
 * stays a view over state it does not own.
 */
const BoardCanvas = ({
  board,
  tool,
  shape,
  colour,
  readOnly = false,
  onPlaceComment,
  commentsOverlay,
  /** What sits on the board under the ink — opened pages and pictures. It
   *  can be picked up under the cursor, and is drawn over with any other
   *  tool, exactly like a note. */
  underlay,
  /** The zoom the board is rendered under. Screen pixels have to be divided
   *  by it or everything lands at the wrong place once you zoom. */
  scale = 1,
  /** Inside another surface the board brings no card of its own. */
  bare = false,
  /** Strokes other people are drawing right now — drawn, never stored. */
  liveStrokes = [],
  /** Called as this pointer draws, so everyone else sees the line appear
   *  rather than waiting for the pen to lift. */
  onStrokeProgress,
  onStrokeEnded,
  /** Called once a placing tool has placed its thing, so the board can hand
   *  the pointer back to the cursor. */
  onToolUsed,
  /**
   * How far past its box, in board pixels, the board still takes presses.
   *
   * On an endless canvas the box is only the first screenful: once it is
   * panned away there is board with nothing under it to draw on. With a
   * reach the surface extends that far in every direction, and what is on
   * it is offset back by the same amount, so nothing stored moves.
   */
  reach = 0,
  /** The cursor over empty board under the cursor tool — "grab" where a
   *  press there moves the canvas. */
  idleCursor = "default",
}) => {
  const surfaceRef = useRef(null);
  const [liveStroke, setLiveStroke] = useState(null);
  const [liveShape, setLiveShape] = useState(null);
  const [hoveredItemId, setHoveredItemId] = useState(null);

  /**
   * Keeping the add handles up long enough to reach them.
   *
   * They float outside the item, so the pointer crosses a gap belonging to
   * neither on its way to one — which fired a leave and took the handles
   * away just before the click landed. Hiding waits a moment now, and
   * arriving anywhere inside the item, or on a handle, cancels it.
   */
  const hoverExit = useRef(null);
  const holdHover = useCallback((id) => {
    clearTimeout(hoverExit.current);
    setHoveredItemId(id);
  }, []);
  const releaseHover = useCallback((id) => {
    clearTimeout(hoverExit.current);
    hoverExit.current = setTimeout(
      () => setHoveredItemId((prev) => (prev === id ? null : prev)),
      HOVER_GRACE_MS
    );
  }, []);
  useEffect(() => () => clearTimeout(hoverExit.current), []);

  const pointAt = useCallback(
    (e) => {
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: (e.clientX - rect.left) / scale - reach,
        y: (e.clientY - rect.top) / scale - reach,
      };
    },
    [scale, reach]
  );

  const handlePointerDown = (e) => {
    if (readOnly) return;
    // Under the cursor a press on an item is that item's drag. With any other
    // tool armed the item is inert and the press is the tool's.
    if (tool === TOOLS.SELECT && e.target.closest("[data-board-item]")) return;

    const point = pointAt(e);

    if (tool === TOOLS.SELECT) {
      board.setSelectedId(null);
      return;
    }

    if (PLACES_ON_CLICK.has(tool)) {
      if (tool === TOOLS.COMMENT) {
        const rect = surfaceRef.current.getBoundingClientRect();
        onPlaceComment?.({
          xPct: (point.x / rect.width) * 100,
          yPct: (point.y / rect.height) * 100,
          clientX: e.clientX,
          clientY: e.clientY,
        });
        onToolUsed?.();
        return;
      }
      const created = board.add({
        kind: tool === TOOLS.STICKY ? "sticky" : "text",
        // Placed so the pointer lands where the typing starts, not at a corner.
        x: point.x - (tool === TOOLS.STICKY ? 84 : 8),
        y: point.y - (tool === TOOLS.STICKY ? 20 : 12),
        colour,
        text: "",
        autoFocus: true,
      });
      board.setSelectedId(created.id);
      onToolUsed?.();
      return;
    }

    if (DRAGS_TO_DRAW.has(tool)) {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      if (tool === TOOLS.DRAW) {
        const id = `stroke-${Date.now()}`;
        setLiveStroke({ id, points: [point], colour, width: STROKE_WIDTH });
        onStrokeProgress?.({ id, points: [point], colour, width: STROKE_WIDTH });
      } else {
        setLiveShape({ origin: point, x: point.x, y: point.y, width: 0, height: 0 });
      }
    }
  };

  const handlePointerMove = (e) => {
    if (liveStroke) {
      const point = pointAt(e);
      setLiveStroke((prev) => {
        const next = { ...prev, points: [...prev.points, point] };
        // Throttled upstream; sending the whole run of points keeps a
        // receiver correct even if it missed the previous one.
        onStrokeProgress?.(next);
        return next;
      });
      return;
    }
    if (liveShape) {
      const point = pointAt(e);
      const { origin } = liveShape;
      setLiveShape({
        origin,
        x: Math.min(origin.x, point.x),
        y: Math.min(origin.y, point.y),
        width: Math.abs(point.x - origin.x),
        height: Math.abs(point.y - origin.y),
      });
    }
  };

  /**
   * Add a sibling beside `item` and join the two.
   *
   * The new item copies the kind and look of the one it came from, so a
   * chain of notes stays a chain of notes, and the teacher only has to pick
   * the colour once.
   */
  const addConnected = useCallback(
    (item, direction) => {
      const { x, y } = spawnPosition(item, direction);
      // Everything but its identity: `add` mints the new id, and spreading
      // the old one over it would hand two items the same key.
      const { id: _ignored, ...look } = item;
      const created = board.add({
        ...look,
        x,
        y,
        text: "",
        autoFocus: item.kind === "sticky",
      });
      board.connect(item.id, created.id, item.colour);
      board.setSelectedId(created.id);
      setHoveredItemId(created.id);
    },
    [board]
  );

  /**
   * Tab adds the next one, as a board is usually filled.
   *
   * The plus handles are the way in with a mouse; this is the way in
   * without one, and it means a chain can be built without the hand
   * leaving the keyboard between notes.
   */
  useEffect(() => {
    if (readOnly) return undefined;
    const onKey = (e) => {
      if (e.key !== "Tab" || e.metaKey || e.ctrlKey || e.altKey) return;
      // Typing inside a note is the usual way to arrive here: you write one,
      // press Tab, and write the next. So a field belonging to a board item
      // is fine; any other field is somebody filling in a form.
      const el = e.target;
      const inItem = el && el.closest && el.closest("[data-board-item]");
      if (!inItem && el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      const item = board.items.find((it) => it.id === board.selectedId);
      if (!item || !CONNECTABLE.has(item.kind)) return;
      e.preventDefault();
      addConnected(item, DIRECTIONS.find((d) => d.id === (e.shiftKey ? "left" : "right")));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [board.items, board.selectedId, addConnected, readOnly]);

  /**
   * Delete removes what is selected; Escape lets go of it.
   *
   * Ignored while typing — Backspace in a note is deleting a letter, not the
   * note. That is the one place a field inside an item is not the board.
   */
  useEffect(() => {
    if (readOnly) return undefined;
    const onKey = (e) => {
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (!board.selectedId) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        board.remove(board.selectedId);
      } else if (e.key === "Escape") {
        board.setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [board, readOnly]);

  const handlePointerUp = () => {
    if (liveStroke) {
      // A tap is not a stroke.
      if (liveStroke.points.length > 1) {
        board.addStroke({ ...liveStroke, id: liveStroke.id });
      }
      onStrokeEnded?.(liveStroke.id);
      setLiveStroke(null);
    }
    if (liveShape) {
      if (liveShape.width >= MIN_SHAPE || liveShape.height >= MIN_SHAPE) {
        const created = board.add({
          kind: "shape",
          shape,
          x: liveShape.x,
          y: liveShape.y,
          width: liveShape.width,
          height: liveShape.height,
          colour,
        });
        board.setSelectedId(created.id);
        onToolUsed?.();
      }
      setLiveShape(null);
    }
  };

  const connectors = board.items.filter(isConnector);
  const selectedItem = board.items.find((it) => it.id === board.selectedId && !isConnector(it));
  const nodes = board.items.filter((it) => !isConnector(it));
  const itemsById = Object.fromEntries(nodes.map((it) => [it.id, it]));

  return (
    <Box
      ref={surfaceRef}
      data-board-canvas
      position={reach ? "absolute" : "relative"}
      left={reach ? `${-reach}px` : undefined}
      top={reach ? `${-reach}px` : undefined}
      width={reach ? `calc(100% + ${2 * reach}px)` : "100%"}
      height={reach ? `calc(100% + ${2 * reach}px)` : "100%"}
      minH={reach ? undefined : bare ? "100%" : "460px"}
      overflow={bare ? "visible" : "hidden"}
      borderRadius={bare ? "0" : "14px"}
      bg={bare ? "transparent" : c.surface}
      border={bare ? "none" : "1px solid"}
      borderColor={bare ? undefined : c.borderSoft}
      cursor={cursorFor(tool, readOnly, idleCursor)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      sx={{
        touchAction: "none",
        "& [data-inert], & [data-inert] *": { pointerEvents: "none !important" },
      }}
    >
      <Box
        // Everything on the board, put back where it was stored. Without a
        // reach this is the surface exactly.
        position="absolute"
        left={`${reach}px`}
        top={`${reach}px`}
        width={reach ? `calc(100% - ${2 * reach}px)` : "100%"}
        height={reach ? `calc(100% - ${2 * reach}px)` : "100%"}
      >
        {underlay && (
          <Box data-inert={tool !== TOOLS.SELECT ? "" : undefined}>{underlay}</Box>
        )}

        {/* Everyone's ink: what is stored, what this pointer is drawing, and
            what other people are drawing at this moment. */}
        <InkLayer
          strokes={board.strokes}
          liveStroke={liveStroke}
          remoteStrokes={liveStrokes}
          // Under the cursor a line can be picked up, moved and deleted like
          // a note; with a tool armed it is only ink to draw over.
          selectable={!readOnly && tool === TOOLS.SELECT}
          selectedId={board.selectedId}
          scale={scale}
          onSelect={board.setSelectedId}
          onMoveEnd={(id, points) => board.updateStroke(id, { points })}
        />

        {/* Under the items, so a line never covers the note it points at. */}
        <ConnectorLayer
          connectors={connectors}
          itemsById={itemsById}
          readOnly={readOnly}
          onRemove={board.remove}
        />

        {nodes.map((item) => {
          const Component = ITEM_COMPONENTS[item.kind];
          if (!Component) return null;
          const canChain = !readOnly && tool === TOOLS.SELECT && CONNECTABLE.has(item.kind);
          return (
            <Box
              key={item.id}
              data-board-item
              // With a placing tool armed, what is already there steps aside:
              // pressing on a note with the shape tool draws a shape, as it
              // does in FigJam and Miro, instead of picking the note up.
              data-inert={tool !== TOOLS.SELECT ? "" : undefined}
              onPointerEnter={() => holdHover(item.id)}
              onPointerLeave={() => releaseHover(item.id)}
            >
              <Component
                item={item}
                isSelected={board.selectedId === item.id}
                readOnly={readOnly}
                onChange={(patch) => board.update(item.id, patch)}
                // A drag is one change to undo: the board is marked when it is
                // picked up, and the frames of the move are not recorded.
                onMove={({ x, y }) => board.update(item.id, { x, y }, { record: false })}
                onSelect={() => {
                  board.checkpoint();
                  board.setSelectedId(item.id);
                  board.bringToFront(item.id);
                }}
                onRemove={() => board.remove(item.id)}
              />
              {canChain && hoveredItemId === item.id && (
                <AddHandles item={item} onAdd={(d) => addConnected(item, d)} />
              )}
            </Box>
          );
        })}

        {/* The selected item's frame and resize handles. Only under the
            cursor: with a placing tool armed a click means "put one here". */}
        {!readOnly && tool === TOOLS.SELECT && selectedItem && RESIZABLE.has(selectedItem.kind) && (
          <SelectionFrame
            item={selectedItem}
            scale={scale}
            onResizeStart={board.checkpoint}
            onResize={(patch) => board.update(selectedItem.id, patch, { record: false })}
          />
        )}

        {/* the shape being dragged out */}
        {liveShape && (liveShape.width > 0 || liveShape.height > 0) && (
          <Box position="absolute" inset="0" pointerEvents="none">
            <ShapeItem
              item={{ ...liveShape, shape, colour, id: "preview" }}
              isSelected={false}
              readOnly
              onMove={() => {}}
              onSelect={() => {}}
            />
          </Box>
        )}

        {commentsOverlay}
      </Box>
    </Box>
  );
};

export default BoardCanvas;
